import React from 'react';
import {
  List,
  Datagrid,
  EditButton,
  CreateButton,
  FunctionField,
  ListProps,
  BulkDeleteButton,
  useListContext,
} from 'react-admin';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import DeleteWithConfirmButton from "../../../components/DeleteWithConfirmButton.tsx";

const RoleListHeader = () => {
  const { total } = useListContext();

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2.5,
        borderRadius: '14px',
        border: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a', mb: 0.5 }}>
            Role Management
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Manage role definitions and permission assignments.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={`Total Roles: ${total ?? 0}`}
            sx={{
              height: 32,
              fontWeight: 700,
              bgcolor: '#f1f5f9',
              color: '#334155',
            }}
          />
          <CreateButton
            label="New Role"
            sx={{
              px: 2,
              height: 34,
              borderRadius: '10px',
              fontWeight: 700,
              backgroundColor: '#0f172a',
              color: '#fff',
              '&:hover': { backgroundColor: '#111827' },
              '& .MuiButton-startIcon, & .MuiSvgIcon-root': { color: '#fff' },
            }}
          />
        </Stack>
      </Stack>
    </Paper>
  );
};

const ActionButtons = ({ record }: { record?: any }) => {
  if (!record) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      <EditButton
        record={record}
        sx={{
          textTransform: 'capitalize',
          '& span': { mr: '2px' },
        }}
      />
      <DeleteWithConfirmButton record={record} resource="permissionRoles" />
    </Box>
  );
};

const RoleList = (props: ListProps) => (
  <List
    sx={{
      marginTop: '20px',
      px: 2.5,
      '& .RaList-content': {
        p: '20px !important',
      },
    }}
    empty={false}
    {...props}
    actions={false}
  >
    <RoleListHeader />
    <Paper
      elevation={0}
      sx={{
        p: 0.75,
        borderRadius: '14px',
        overflow: 'visible',
        border: '1px solid #e5e7eb',
        boxShadow: '0 6px 18px rgba(15, 23, 42, 0.05)',
        bgcolor: '#fff',
      }}
    >
      <Datagrid
        rowClick={false}
        isRowSelectable={() => true}
        bulkActionButtons={<BulkDeleteButton label="Batch Delete" />}
        sx={{
          borderRadius: '10px',
          overflow: 'hidden',
          '& .RaDatagrid-table': {
            tableLayout: 'fixed',
          },
          '& .RaDatagrid-headerCell': {
            textAlign: 'center',
            bgcolor: '#f8fafc',
            fontWeight: 700,
            color: '#334155',
            py: 1.25,
          },
          '& .RaDatagrid-rowCell': {
            textAlign: 'center',
            py: 1.1,
            verticalAlign: 'middle',
          },
          '& .RaDatagrid-headerCell:first-of-type, & .RaDatagrid-rowCell:first-of-type': {
            pl: 2,
          },
          '& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type': {
            pr: 1.5,
          },
          '& .RaDatagrid-row:nth-of-type(even)': {
            bgcolor: '#fbfdff',
          },
          '& .RaDatagrid-row:hover': {
            bgcolor: '#f8fafc',
          },
             '& .RaDatagrid-headerCell.MuiTableCell-paddingCheckbox': {
               pr: '25px !important',
             },
        }}
      >
        <FunctionField
          label="Role ID"
          render={(record: any) => (
            <Chip
              size="small"
              label={`#${record?.id ?? '-'}`}
              sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 700 }}
            />
          )}
        />
        <FunctionField
          label="Role Name"
          render={(record: any) => (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#e2e8f0',
                  color: '#0f172a',
                  fontSize: '0.8rem',
                  fontWeight: 800,
                }}
              >
                {String(record?.roleName || '?').charAt(0).toUpperCase()}
              </Box>
              <Chip
                size="small"
                label={record?.roleName || '-'}
                sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}
              />
            </Stack>
          )}
        />
        <FunctionField
          label="Description"
          render={(record: any) => (
            <Typography
              variant="body2"
              sx={{
                color: record?.description ? '#475569' : '#94a3b8',
                maxWidth: 320,
                mx: 'auto',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={record?.description || '-'}
            >
              {record?.description || 'No description'}
            </Typography>
          )}
        />
        <FunctionField label="Operations" render={(record: any) => <ActionButtons record={record} />} />
      </Datagrid>
    </Paper>
  </List>
);

export default RoleList;
